import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation } from '../../trpc/trpc';

export interface RenameWorkspaceState {
  rename: (name: string) => Promise<void>;
  isPending: boolean;
  error: unknown;
  /** The last call succeeded. A new call sets it back to false. */
  isSaved: boolean;
}

/**
 * `workspaces.rename` (design: workspaces d2). An empty name goes back to the default name. After
 * a save, the switcher and the Team tab read the new name again.
 */
export function useRenameWorkspace(): RenameWorkspaceState {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const [isSaved, setSaved] = useState(false);
  const { mutateAsync, isPending, error } = useTRPCMutation({
    mutationFn: (name: string) => trpcClient.workspaces.rename.mutate({ name }),
    onMutate: () => setSaved(false),
    onSuccess: async () => {
      setSaved(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpcAPI.workspaces.list.queryKey() }),
        queryClient.invalidateQueries({ queryKey: trpcAPI.team.list.queryKey() }),
      ]);
    },
  });
  const rename = useCallback(
    async (name: string) => {
      await mutateAsync(name);
    },
    [mutateAsync]
  );
  return { rename, isPending, error, isSaved };
}
